/* eslint-disable no-underscore-dangle */
import { Component, AfterContentChecked, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router, RoutesRecognized, Params } from '@angular/router';
import { Location } from '@angular/common';

import { ConfirmEventType } from 'primeng/api';

import {
    DynamicComponent,
    DynamicObjects,
    GenericValue,
    HisAuthService,
    HisBaseGlobalsService,
    HisNotificationService,
    HisConfirmationService,
    HisUiService,
    HisGlobalDataService,
    HisNavigationService,
    HisValueGroup,
    HisDynamicFormGroupUtils
} from 'hisng';

import { CONFIRMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/confirmation-dialog.component';
import { INFORMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/information-dialog.component';
import {
    DATA_SERVICE_KEY_CHILD_PUBLICATION,
    DATA_SERVICE_KEY_PARENT_PUBLICATION,
    DATA_SERVICE_KEY_PUBLICATION
} from '../../../model/constants';

import { PublicationModuleOptions } from '../../../publication-module.options';
import { DynamicContentUtils } from 'app/fs/res/common/service/dynamic-content/dynamic-content-utils';
import { DynamicContentService } from 'app/fs/res/common/service/dynamic-content/dynamic-content.service';
import { ResTabMenuItem } from 'app/fs/res/common/model/core/tab-menu-item';
import { Duplicate } from 'app/fs/res/common/model/duplicate';
import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';

import { PublicationAuthService } from '../../../service/publication-auth.service';
import { PublicationService } from 'app/fs/res/publication/service/publication.service';
import { Publication } from 'app/fs/res/publication/model/publication';
import { PublicationTypeRelationService } from '../../../service/publication-type-relation.service';
import { PublicationType } from '../../../model/publication-type';

@Component({
    selector: 'res-publication-edit-form',
    templateUrl: './publication-edit-form.component.html',
})
export class PublicationEditFormComponent implements AfterContentChecked, OnInit, OnDestroy {

    public readonly moduleOptions = PublicationModuleOptions;

    // keys of confirmation dialogs
    public readonly CONFIRM_BACK_TO_CHILD_DIALOG_KEY = 'confirmBackNavigationDialogKey';
    public readonly CONFIRM_SAVE_DESPITE_DUPLICATE_DIALOG_KEY = 'confirmSaveDespiteDuplicateDialogKey';

    @ViewChild('tabData', {read: ElementRef} ) tabData: ElementRef ;

    public publication: Publication = new Publication();
    public isInitialPublicationType = true;
    public publicationId: number = undefined;

    public showBackButton = false;
    public showSearchButton = false;
    public showDeleteButton = true; // delete _draft_ button

    public isDuplicate = false;
    public prevPage: string;

    public tabMenuItems: ResTabMenuItem[];
    public publicationTypeChildParentMap: {[key: string]: PublicationType[]};

    private publicationStatusList: GenericValue[];
    private hasStatusAuthority = false;
    private isDraft = true;
    private showAbstractTab = false;
    navigateToCreateParentOnNextSave = false;
    navigateToEditParentOnNextSave = false;
    public publicationType: GenericValue;
    public childPublication: Publication;
    public parentPublication: Publication;
    public reloadTrigger = true;

    public statusDataEntryHiskey = 1;
    public statusForValidationHiskey = 2;
    public statusValidatedHiskey = 3;
    private savablePublicationStatus = new Set<number>(
        [this.statusDataEntryHiskey, this.statusForValidationHiskey, this.statusValidatedHiskey]);

    isAcceptingChanges=false;

    public possiblePublicationTypesLoaded = !this.globalDataService.getData(DATA_SERVICE_KEY_CHILD_PUBLICATION);

    private duplicatesByTitle: Duplicate[];
    private duplicatesByIdentifier: Duplicate[];
    private prevDynComp: DynamicComponent;
    changeRequestValueGroup = new HisValueGroup( 5 );
    selectedTabIndex = 0;

    private staticAttributesOfChangeRequest = [
        { tab: 'fs.res.common.mainData', attributes: ['visibilityValue', 'keywords'] },
        { tab: 'fs.res.common.classifications', attributes:  ['subjectAreas', 'researchAreas', 'researchAreasKdsf'] }
    ];

    constructor(
        public auth: HisAuthService,
        public hisBaseGlobalsService: HisBaseGlobalsService,
        private publicationAuthService: PublicationAuthService,
        private confirmationService: HisConfirmationService,
        private notificationService: HisNotificationService,
        private publicationService: PublicationService,
        private route: ActivatedRoute,
        private router: Router,
        private hisUiService: HisUiService,
        private location: Location,
        private dynamicContentUtils: DynamicContentUtils,
        private dynamicContentService: DynamicContentService,
        private dynamicFormGroupUtils: HisDynamicFormGroupUtils,
        private globalDataService: HisGlobalDataService,
        private navigationService: HisNavigationService,
        private publicationTypeRelationService: PublicationTypeRelationService,
        private ddUtils: DataDictionaryUtils ) {
    }

    ngAfterContentChecked() {
        // dynamic content is only accessible with module 'HISINONERES'
        if (!this.hisBaseGlobalsService.isAnyModuleActive('HISINONERES')) {
            this.publication._dynamicComponent = null;
        }
        this.dynamicContentUtils.setupDynamicTabsHeader(this.publication._dynamicComponent, this.tabMenuItems);
    }

    ngOnInit() {
        this.showAbstractTab = this.hisBaseGlobalsService.isAnyModuleActive('HISINONERES')
            && this.ddUtils.isVisible( 'fs.res.publication.Publication.textAbstract' );

        this.initializeTabMenuItems();
        this.publication.languages = [];
        this.publication.projects = [];
        this.publication.researchInfrastructures = [];

        this.route.params.subscribe( (params: Params) => {
            if (this.router.routerState.snapshot.url.includes('edit')) {
                if (params.publicationId) {
                    this.publicationId = parseInt(params.publicationId, 10);
                    this.setInitialPublicationType(false);
                }
            }
        });

        // read parameters for back button usw.
        this.route.queryParams.subscribe( (queryParams: Params) => {
            if (queryParams.tab) {
                this.selectedTabIndex = queryParams.tab;
            }

            // #299252 wir wollen keinen Zurück-Button bei "Publikation anlegen"
            this.showBackButton = queryParams.showBackButton ? queryParams.showBackButton === 'true' : this.showBackButton;
            // #294160 "Zurück zum Suchergebnis"-Button
            this.showSearchButton = queryParams.showSearchButton ?
                queryParams.showSearchButton === 'true' : this.navigationService.hasPreviousSearchResultUrl();

            this.showDeleteButton = queryParams.showDeleteButton ? queryParams.showDeleteButton === 'true' : this.showDeleteButton;
            this.isDuplicate = queryParams.isDuplicate ? queryParams.isDuplicate === 'true' : this.isDuplicate;
            this.prevPage = queryParams.prevPage;

            let showMsg = !!queryParams.showMsg && queryParams.showMsg === 'true';
            let url = this.router.routerState.snapshot.url.replace('?fromForm=true', '').replace('&showMsg=true', '');
            this.location.replaceState(url);

            let childPublicationData: Publication = this.globalDataService.getData(DATA_SERVICE_KEY_CHILD_PUBLICATION);
            let parentPublicationData: Publication = this.globalDataService.getData(DATA_SERVICE_KEY_PARENT_PUBLICATION);
            if (childPublicationData) {
                this.childPublication = childPublicationData;
                this.presetPublicationType();
                this.globalDataService.removeData(DATA_SERVICE_KEY_CHILD_PUBLICATION);
                if (showMsg) {
                    this.showSuccessfullMessage(this.childPublication);
                }
            }
            if (parentPublicationData) {
                this.parentPublication = parentPublicationData;
                this.globalDataService.removeData(DATA_SERVICE_KEY_PARENT_PUBLICATION);
                if (showMsg) {
                    this.showSuccessfullMessage(this.parentPublication);
                }
            }
        });

        this.router.events.subscribe( (event: any) => {
            // type Event or RouterEvent would not compile, see https://github.com/angular/angular/issues/43124
            if (event instanceof RoutesRecognized) {
                let rr = event as RoutesRecognized;
                if (rr.state.url.includes('pub/search') || rr.state.url.includes('submenu')) {
                    this.globalDataService.removeData(DATA_SERVICE_KEY_PUBLICATION);
                }
            }
        });

        if (this.publicationId) {
            if (!this.publicationCachedInGlobalDataService()) {
                this.publicationService.getPublicationById(this.publicationId).subscribe( (p: Publication) => {
                    if ( this.parentPublication ) {
                        p.parent = this.parentPublication;
                    }
                    this.publication  = p;
                    this.publication.researchInfrastructures = p.researchInfrastructures;
                    this.publication.changeRequest = p.changeRequest;
                    this.publicationService.canEditPublication(this.publication.id).subscribe( (canEdit: boolean) => {
                        this.isDraft = canEdit;
                        if(this.isDraft) {
                            this.initTabsIncludingChangeRequests(this.publication);
                        }
                    });
                    console.log('read publication', this.publication);
                });
            }
        } else {
            this.publicationCachedInGlobalDataService();
        }

        this.publicationTypeRelationService.getPublicationTypeChildParentMap().subscribe( (val: {[key: string]: PublicationType[]}) => {
            this.publicationTypeChildParentMap = val;
            this.presetPublicationType();
        });

        this.hasStatusAuthority = this.publicationAuthService.hasStatusAuthority();
        this.initStatus();
    }

    tabChanged(newTabIndex: number) {
        this.selectedTabIndex = newTabIndex;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {tab: newTabIndex},
            queryParamsHandling: 'merge'
        });
    }

    changeRequestExists() {
        return this.publication.changeRequest != null;
    }

    showSuccessfullMessage(publication: Publication, changeRequestSaved = false) {
        if (changeRequestSaved) {
            this.notificationService.successI18nRes('fs.res.publication.changeRequest.successfullySaved');
        } else if (publication?.status.hiskeyId === this.statusDataEntryHiskey) {
            this.notificationService.successI18nRes(
                'fs.res.publication.Publication.successfullySavedDataEntry',
                publication.publicationType.defaulttext
            );
        } else if (publication?.status.hiskeyId === this.statusForValidationHiskey) {
            this.notificationService.successI18nRes(
                'fs.res.publication.Publication.successfullySavedForValidation',
                publication.publicationType.defaulttext
            );
        } else if (publication?.status.hiskeyId === this.statusValidatedHiskey) {
            this.notificationService.successI18nRes(
                'fs.res.publication.Publication.successfullySavedValidated',
                publication.publicationType.defaulttext
            );
        }
    }

    showSavingFailedMessage(publication: Publication) {
        this.notificationService.errorI18nRes(
            'fs.res.publication.Publication.notSavedDueToBadStatus',
            publication.publicationType.defaulttext
        );
    }

    /* reset input by click */
    reset() {
        if (this.publication.id) {
            this.publication._dynamicComponent = null;
            this.publicationService.getPublicationById(this.publicationId).subscribe( (p: Publication) => {
                this.publication = p;
                this.publication.languages = p.languages;
            });
        } else {
            let publicationType = this.publication.publicationType;
            let peerReviewedProcess = this.publication.peerReviewedProcess;
            this.publication = new Publication();
            this.publication.publicationType = publicationType;
            this.publication.peerReviewedProcess = peerReviewedProcess;
            this.publication.languages = [];
            this.publication.globalIdentifiers = [];
            this.publication.projects = [];
            this.publication.researchInfrastructures = [];
            // dynamic content
            this.generateDynamicContent();
        }
    }

    /* save publication */
    savePublication(navigateToChild = false) {
        if ( !this.savablePublicationStatus.has(this.publication?.status?.hiskeyId) ) {
            this.showSavingFailedMessage(this.publication);
            return;
        }

        if (this.duplicatesByTitle?.length > 0 || this.duplicatesByIdentifier?.length > 0) {
            console.log('There are duplicates -> ask user to confirm save...');
            this.confirmationService.confirm({
                key: this.CONFIRM_SAVE_DESPITE_DUPLICATE_DIALOG_KEY,
                message: this.hisUiService.translate('fs.res.publication.Publication.saveDespiteDuplicateQuestion'),
                accept: () => {
                    this.doSavePublication(navigateToChild);
                },
                reject: () => {
                    return; // Speichern abbrechen
                }
            });
        } else {
            this.doSavePublication(navigateToChild);
        }
    }

    doSavePublication(navigateToChild: boolean) {
        // set project dynamic component object values
        this.dynamicFormGroupUtils.setDynamicComponentObject(this.publication);
        this.setStaticValuesOfChangeRequest();
        console.log('publication._dynamicComponent before save =', this.publication._dynamicComponent);

        let message = this.publication.id ? 'edit' : 'create';
        this.publicationService.savePublication(this.publication, this.childPublication).subscribe( (p: Publication) => {
            const changeRequestSaved = this.changeRequestExists();
            this.publication = p;
            this.publicationId = p.id;
            this.publication.publicationCreatorType = p.publicationCreatorType;
            this.publicationService.canEditPublication(this.publication.id).subscribe( (canEdit: boolean) => {
                this.isDraft = canEdit;
                if(this.isDraft) {
                    this.initTabsIncludingChangeRequests(this.publication);
                }
            });

            if (this.navigateToCreateParentOnNextSave) {
                this.location.replaceState(`pub/edit/${this.publication.id}`);
                this.navigationService.setLastBackUrl( window.location.href );
                this.globalDataService.setData( DATA_SERVICE_KEY_CHILD_PUBLICATION, this.publication );
                this.publicationService.navigateToCreateParentPublication(true);
                this.navigateToCreateParentOnNextSave = false;
            } else if (this.navigateToEditParentOnNextSave && this.publication.parent) {
                this.location.replaceState(`pub/edit/${this.publication.id}`);
                this.navigationService.setLastBackUrl( window.location.href );
                this.globalDataService.setData( DATA_SERVICE_KEY_CHILD_PUBLICATION, this.publication );
                this.publicationService.navigateToEditParentPublication(true, this.publication.parent.id);
                this.navigateToEditParentOnNextSave = false;
            } else if (this.doNavigateToChild(navigateToChild)) {
                this.globalDataService.setData( DATA_SERVICE_KEY_PARENT_PUBLICATION, this.publication );
                this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=> {
                    // hier funktioniert this.navigationService.navigateToLastBackUrl() leider nicht richtig,
                    // weil wir auch showMsg: true zu den URL-Parametern hinzufügen müssen
                    this.router.navigate([`pub/edit/${this.childPublication.id}`],
                        { queryParams: {
                            fromForm: true,
                            showMsg: true,
                            showBackButton: this.showBackButton,
                            showSearchButton: this.navigationService.hasPreviousSearchResultUrl()
                        } }
                    );
                });
            } else if (this.isAdmin() || 2 !== this.publication.status.hiskeyId) {
                // Entweder die Publikation ist in einem anderen Status als "zur Validierung",
                // oder sie ist im Status "zur Validierung" und der aktuelle User hat das Recht den Status zu bearbeiten
                // -> dann dürfen wir weiter bearbeiten
                this.showSuccessfullMessage(this.publication, changeRequestSaved);
                this.location.replaceState(`pub/edit/${this.publication.id}`);
            } else if (this.hisBaseGlobalsService.isAnyModuleActive('HISINONERES')) {
                // Wir haben keine Erlaubnis die Seite weiter zu bearbeiten. Mit aktivem Modul HISINONERES gehen wir dann zur Detailseite.
                console.log('module HISINONERES is active -> navigate to detail page');
                this.router.navigate([`pub/view/${this.publication.id}`], {
                    queryParams: {
                        showSearchButton: this.navigationService.hasPreviousSearchResultUrl(),
                        forValidation: true,
                        msg: message
                    }
                });
            } else if (this.hisBaseGlobalsService.isAnyModuleActive('HISINONEKDSF')) {
                // Wir haben keine Erlaubnis die Seite weiter zu bearbeiten und die Detailseite dürfen wir auch nicht sehen
                // weil das Modul HISINONERES nicht aktiv ist. Da aber Modul HISINONEKDSF aktiv ist können wir zur Suche zurückgehen.
                console.log('module HISINONEKDSF is active -> navigate to search');
                this.router.navigate(['pub/search/'], { queryParams: { saveForValidationSuccess: 1 } });
            } else {
                // Hier ist wohl etwqas schief gelaufen...
                console.log( 'Error: doSavePublication() is called but none of the modules HISINONERES, HISINONEKDSF is active!' );
            }
        });
    }

    private doNavigateToChild(navigateToChild: boolean): boolean {
        if (!this.childPublication) {
            return false;
        }
        if (navigateToChild) {
            return true;
        }
        let statusHiskeyId = this.publication.status.hiskeyId;
        let statusAuthorityLevel = this.publicationAuthService.getEditPublicationStatusAuthorityLevel();
        return (statusHiskeyId === 3 && (this.hasStatusAuthority || statusAuthorityLevel === 3)) || 
            (statusHiskeyId === 2 && statusAuthorityLevel === 2);
    }
    
    /* get the publicationStatus with hiskeyId 2 (zur Validation) and save the publication with this new status */
    savePublicationForValidation() {
        console.log('===> savePublicationForValidation():');
        this.publication.status = this.getStatus(2);
        this.savePublication();
    }

    /* get the publicationStatus with hiskeyId 1 (Dateneingabe) and save the publication with this new status */
    savePublicationAsDraft() {
        console.log('===> savePublicationAsDraft():');
        this.publication.status = this.getStatus(1);
        this.savePublication();
    }

    /* delete publication */
    deletePublication() {
        this.publicationService.canDeletePublication(this.publication.id).subscribe( (canDelete: string) => {
            if (canDelete === '') {
                this.confirmDeletion('fs.res.publication.confirmDeletePublication', canDelete);
            } else {
                this.confirmDeletion(canDelete, canDelete);
            }
        });
    }

    public onPublicationTypeChange( event: GenericValue ) {
        this.prevDynComp = this.publication._dynamicComponent;
        this.publication._dynamicComponent = null;
        // dynamic content on publication type
        this.generateDynamicContent( this.prevDynComp.dynamicObjects );
    }

    /* set isInitialPublicationType for initial publicationType selection */
    setInitialPublicationType(isInitialPublicationType: boolean): void {
        if (!this.publicationId) {
            // to generate dynamic content on publication type
            this.generateDynamicContent();
        }
        this.isInitialPublicationType = isInitialPublicationType;
    }

    /*
     * returns true if the current user is not allowed to validate a publication and the publication is still a draft
    */
    isUserEditMode(): boolean {
        return !this.hasStatusAuthority && this.isDraft;
    }

    /*
     * returns true if the current user is not allowed to validate a publication and the publication is still a draft
     * OR the current user is allowed to validate a publication
    */
    isUserEditModeOrAdmin(): boolean {
        return this.isUserEditMode() || this.hasStatusAuthority;
    }

    /*
     * returns true if the current user is allowed to validate a publication
    */
    isAdmin(): boolean {
        return this.hasStatusAuthority;
    }

    private initStatus() {
        this.publicationService.getPublicationStatus().subscribe( (status: GenericValue[]) => {
            this.publicationStatusList = status;
        });
    }

    private getStatus(hiskeyId: number): GenericValue {
        for (let s of this.publicationStatusList) {
            if (s.hiskeyId === hiskeyId) {
                return s;
            }
        }
        return null;
    }

    /**
     * invoke confirmation service to display confirmation-/information dialog before deleting a publication.
     *
     * @param msg dialog message
     * @param canDelete identifier publication can be deleted
     */
    private confirmDeletion(msg: string, canDelete: string) {
        this.confirmationService.confirm({
            key: canDelete === '' ? CONFIRMATION_PDIALOG_KEY : INFORMATION_PDIALOG_KEY,
            message: canDelete === '' ? this.hisUiService.translate(msg, this.publication.title) : canDelete,
            accept: () => {
                if (canDelete === '') {
                    this.publicationService.deletePublication(this.publication.id).subscribe( (deleteSuccess: number) => {
                        if (deleteSuccess === 1) {
                            this.router.navigate(['/pub/search'], { queryParams: { deleteSuccess: 1 } });
                        }
                    });
                }
            }
        });
    }

    confirmBackNavigation() {
        console.log('confirmBackNavigation() this.showBackButton: ' , this.showBackButton);
        this.publicationService.checkIsDirty(this.publication).subscribe((isDirty: boolean) => {
            if (isDirty) {
                this.confirmationService.confirm({
                    key: this.CONFIRM_BACK_TO_CHILD_DIALOG_KEY,
                    message: this.hisUiService.translate('fs.res.publication.Publication.saveChangesQuestion'),
                    accept: () => {
                        this.publication.status = this.publication.status ?? this.getStatus(1);
                        this.savePublication(true);
                    },
                    reject: (type: ConfirmEventType) => {
                        if (type === ConfirmEventType.REJECT) {
                            this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=> {
                                this.navigationService.navigateToLastBackUrl();
                            });
                        }
                    }
                });
            } else {
                this.router.navigateByUrl('/', {skipLocationChange: true}).then(()=> {
                    this.navigationService.navigateToLastBackUrl();
                });
            }
        });
    }

    /*
     * check if journal was cached in globalDataService.
     */
    private publicationCachedInGlobalDataService(): boolean {
        let dataServicePublication: Publication = this.globalDataService.getData(DATA_SERVICE_KEY_PUBLICATION, true);
        if (dataServicePublication && dataServicePublication.id === this.publicationId) {
            this.publication = dataServicePublication;
            this.isInitialPublicationType = this.publication.publicationType ? false : true;
            this.globalDataService.removeData(DATA_SERVICE_KEY_PUBLICATION);

            // re-render dynamic content and restore values
            this.generateDynamicContent(dataServicePublication._dynamicComponent.dynamicObjects);
        
            return true;
        }
        return false;
    }

    /* generate dynamic content */
    private generateDynamicContent( dynamicObjects?: DynamicObjects[] ) {
        // dynamic content is only accessible with module 'HISINONERES'
        if (this.hisBaseGlobalsService.isAnyModuleActive('HISINONERES')) {
            this.dynamicContentService.generateDynamicContent(DATA_SERVICE_KEY_PUBLICATION, this.getRequestParam()).subscribe(
                (dC: DynamicComponent) => {
                    this.publication._dynamicComponent = dC;
                    if (this.publication.id && this.publication.changeRequest && this.prevDynComp) {
                        this.addPrevValToDynComponent(this.publication._dynamicComponent?.dynamicObjects, this.prevDynComp.dynamicObjects);
                        this.prevDynComp = null;
                    }
                    this.reloadTrigger = !this.reloadTrigger; // reload trigger for dynamicComponent
                    if ( dynamicObjects ) {
                        this.addPrevValToDynComponent(this.publication._dynamicComponent?.dynamicObjects, dynamicObjects);
                        this.dynamicFormGroupUtils.setDynamicComponentObject(this.publication);
                    }
                });
        }
    }

    public addPrevValToDynComponent(dynObjectsTo: DynamicObjects[], dynObjectsFrom: DynamicObjects[]) {
        if (!dynObjectsTo || !dynObjectsFrom) {
            return;
        }
        dynObjectsTo.forEach(dO => {
            const dO2 = dynObjectsFrom.find(g => dO.dynamicClassName === g.dynamicClassName);
            if (dO2) {
                dO.values.forEach(v => {
                    const v2 = dO2.values.find(val => v.attributeName === val.attributeName);
                    if (v2) {
                        v.value = v2.value;
                        v.oldValue = v2.oldValue;
                        v.useOldValue = v2.useOldValue;
                    }
                });
            }
            if(dO.children && dO2.children) {
                this.addPrevValToDynComponent(dO.children, dO2.children);
            }
        });
    }

    /* publication specific dynamic content service request parameter */
    public getRequestParam(): string {
        if (this.publication && this.publication.publicationType) {
            return 'typeHiskeyId=' + this.publication.publicationType.hiskeyId;
        }
        return undefined;
    }

    /* callback to set last window location href */
    public onNavigationLastUrlChange( lastWindowLocation: string ) {
        this.setStaticValuesOfChangeRequest();
        this.navigationService.setLastBackUrl( lastWindowLocation );
    }

    /* initialize default tab menu items */
    private initializeTabMenuItems() {
        this.tabMenuItems = [
            { i18nKey: 'fs.res.common.mainData' },
            ...this.showAbstractTab ? [{ i18nKey: 'fs.res.publication.abstract' }] : [],
            { i18nKey: 'fs.res.common.classifications' }
        ];
        if (this.isDocumentsPanelVisible()) {
            this.tabMenuItems.push({ i18nKey: 'fs.res.common.documents' });
        }
        this.tabMenuItems.push({ i18nKey: 'fs.res.common.research' });
    }

    private presetPublicationType() {
        if (this.childPublication?.publicationType && this.publicationTypeChildParentMap) {
            const possibleParents = this.publicationTypeChildParentMap[this.childPublication?.publicationType.id];
            if (possibleParents?.length === 1 && !this.publication.publicationType) {
                this.publication.publicationType = possibleParents[0];
                this.setInitialPublicationType(false);
            }
            this.possiblePublicationTypesLoaded = true;
        }
    }

    ngOnDestroy() {
        if (this.globalDataService.getData(DATA_SERVICE_KEY_PUBLICATION)) {
            this.dynamicFormGroupUtils.setDynamicComponentObject(this.publication);
        } else {
            this.globalDataService.getData('res_dynamicformGroupValue');
        }
    }

    public onDuplicatesByTitleChange(duplicates: Duplicate[]) {
        console.log('publication-edit-form: duplicates by title changed to', duplicates);
        this.duplicatesByTitle = duplicates;
    }

    public onDuplicatesByIdentifierChange(duplicates: Duplicate[]) {
        console.log('publication-edit-form: duplicates by identifier changed to', duplicates);
        this.duplicatesByIdentifier = duplicates;
    }

    public getHisImageName( tabi18Key: string ): string {
        //evtl  abhängig von bearbeitungsphasen verschiende images liefern
        return 'CIRCLE';

    }

    public initTabsIncludingChangeRequests(publication: Publication) {
        this.tabMenuItems.forEach(tab => tab.showHeaderIcon = false);
        if(publication?.changeRequest) {
            for( let tabItem of this.tabMenuItems){
                let changeFound=false;
                changeFound = this.existChanges(tabItem, publication._dynamicComponent, true );
                if (!changeFound) {
                    changeFound = this.existChanges(tabItem, publication.changeRequest._dynamicComponent, false );
                }

                tabItem.showHeaderIcon = changeFound;
            }
        }
    }

    private existChanges(tabItem: ResTabMenuItem, dynComponent: DynamicComponent, dynAttr: boolean ): boolean {
        const fields = this.staticAttributesOfChangeRequest.find(a => a.tab === tabItem.i18nKey)?.attributes;

        let dynamicGuiChildren = dynComponent.guiComponent.children;
        let dynamicGuiChild = dynamicGuiChildren.find(
            c => c.name === (dynAttr ? tabItem.i18nKey : 'fs.res.common.dynamicContent.tab.dynamicFields') && c.controlType === 'Tab'
        );
        let fieldChanged = false;

        if (dynamicGuiChild && (dynAttr || fields)){
            for(let guiChild of dynamicGuiChild.children) {
                let dynObjects = dynComponent.dynamicObjects;

                for(let field of guiChild.fields){
                    let changeCandidates = dynObjects.find( c =>
                        c.objGuid === field.objectUID && (dynAttr || c.dynamicClassName === 'fs.res.publication.Publication')
                    );
                    if (changeCandidates && !fieldChanged) {
                        fieldChanged = changeCandidates.values.find(
                            v => v.attributeName === field.attributeName &&
                            !this.isEqual(v.value, v.oldValue) &&
                            (dynAttr || fields.some(f => f === v.attributeName))
                        ) != null;
                    }
                }
            }
        }
        return fieldChanged;
    }

    private isEqual(val1: string, val2: string) {
        if([undefined, null, ''].some(v => v === val1) && [undefined, null, ''].some(v => v === val2)) {
            return true;
        } else {
            return val1 === val2;
        }
    }

    private setStaticValuesOfChangeRequest() {
        if (this.publication.changeRequest) {
            const dynClassName = 'fs.res.publication.Publication';
            const obj = this.publication.changeRequest._dynamicComponent.dynamicObjects.find(dO => dO.dynamicClassName === dynClassName);
            if (obj) {
                const valueMap: Record<string, any> = this.changeRequestValueGroup.getJson();
                obj.values.forEach(val => {
                    val.value = valueMap[dynClassName + '.' + val.attributeName];
                });
                this.globalDataService.setData(DATA_SERVICE_KEY_PUBLICATION, this.publication);
            }
        }
    }

    public isDocumentsPanelVisible() {
        let dDEntry = this.hisBaseGlobalsService.getDd('fs.res.publication.documents');
        return ( !dDEntry || !dDEntry.hidden )
            && this.hisBaseGlobalsService.isAnyModuleActive('HISINONERES')
            && this.auth.hasAny('RIGHT_FS_RES_PUBLICATION_CREATE_PUBLICATION_DOCUMENTS');
    }

    public onBeforeNavigate() {
        this.globalDataService.setData(DATA_SERVICE_KEY_PUBLICATION, this.publication);
        if (this.childPublication) {
            this.globalDataService.setData(DATA_SERVICE_KEY_CHILD_PUBLICATION, this.childPublication);
        }
    }

    public back() {
        this.navigationService.navigateToLastBackUrl();
    }

    public backToSearchResult() {
        this.navigationService.navigateToLastSearchResultUrl();
    }
}
